import { animate, style, transition, trigger } from '@angular/animations';
import { Component, computed, effect, inject, NgZone, OnDestroy, signal, untracked } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { CustomRouteService } from 'src/app/pages/custom-route/services';
import { TextToSpeechService } from 'src/chatbot/service/textToSpeech.service';
import dayjs from "dayjs";
import { WebSocketService } from 'src/chatbot/service/webSocket.service';
import { CHANNEL_ATRIL_PANTALLA_NAME, SHOW_HOME_PAGE_EVENT } from '../../config';
import { WebsocketLaravelReverb } from '../../services';

@Component({
  selector: 'app-welcome-view',
  standalone: true,
  imports: [
    
  ],
  animations: [
      trigger('fadeOut', [
          transition(':leave', [
              animate('500ms cubic-bezier(0.4, 0, 0.2, 1)',
                  style({ opacity: 0 }))
          ])
      ])
  ],
  templateUrl: './welcome-view.component.html',
  styleUrls: ['./welcome-view.component.scss'],
})
export class WelcomeViewComponent implements OnDestroy {
  private _ngZone = inject(NgZone);
  private _ttsService = inject(TextToSpeechService);
  private _customRouteService = inject(CustomRouteService);
  private _webSocketService = inject(WebSocketService);
  private _websocketLaravelReverbService = inject(WebsocketLaravelReverb);

  private token = computed(() => this._customRouteService.token());
  startWheTokenReadyEffect = effect(() => {
    if (!this.token())
      return;

    this.getWelcomeData();
  })

  protected currentIndex = signal(0);
  protected fadeState = signal<'fadein' | 'fadeout'>('fadein');
  protected visibleText = signal('');
  protected checkCurrentIndexIsLast = signal(false);
  
  private _fadeTimeoutId: ReturnType<typeof setTimeout>;
  private _increaseIndexTimeoutId: ReturnType<typeof setTimeout>;
  private _typewriterIntervalId: ReturnType<typeof setInterval>;
  private _animationIntervalId: ReturnType<typeof setInterval>;

  public welcomeMessage = signal(false);

  public enableWelcomeMessage(){
    this.welcomeMessage.set(true);
  }

  protected textToSpeechMock = "Es un placer darte la bienvenida a Madrid. Soy Cicerone y te acompañaré en esta aventura. Ya tengo lista tu ruta personalizada. Descubramos juntos todo lo que tienes planeado para hoy.";
  protected welcomeMessageListMock = [
    "Es un placer darte la bienvenida a Madrid. Soy Cicerone y te acompañaré en esta aventura.",
    "Ya tengo lista tu ruta personalizada. Descubramos juntos todo lo que tienes planeado para hoy.",
  ];

  private welcomeMessageList = signal([]);
  private fullWelcomeMessage = computed(() => !this.welcomeMessageList()?.length ? null : this.welcomeMessageList()?.join(' '));
  startPlayingAudioEffect = effect(() => {
    if (!this.fullWelcomeMessage())
      return;

    this.playWelcomeMessage();
  })


  messageIterationAnimation = effect(() => {
    if (!this.welcomeMessage()) 
      return;

    const idx = this.currentIndex();
    const messageArray = !this.welcomeMessageList()?.length ? this.welcomeMessageListMock : this.welcomeMessageList();
    const message = messageArray[idx];

    this.visibleText.set('');
    this.fadeState.set('fadein');
    this.checkCurrentIndexIsLast.set(false);

    let i = 0;
    clearInterval(this._typewriterIntervalId);

    this._typewriterIntervalId = setInterval(async () => {
      if (i < message.length) {
        this.visibleText.update(t => t + message[i++]);
        return;
      }

      clearInterval(this._typewriterIntervalId);

      const isLast = idx === messageArray.length - 1;
      if (isLast){
        untracked(() => {
          this._webSocketService.sendWelcome();
          this._syncronizeHomeInitialization();
        })
      }
      this.fadeState.set('fadeout');
      await this.sleep(400);

      this.checkCurrentIndexIsLast.set(isLast);
      if (!isLast) 
        this.currentIndex.set(idx + 1);

    }, 65);
  }, { allowSignalWrites: true });

  ngOnInit(): void {
    if ('paintWorklet' in (CSS as any)) {
      (CSS as any).paintWorklet.addModule(
        '../../../../../../assets/fitur-2026/worklets/ring-particles.js'
      );
    }
    
    // Espera 200ms antes de iniciar el hack para dar tiempo al worklet
    setTimeout(() => {
      const welcomeEl = document.getElementById('welcome');
      let tick = 0;
      this._ngZone.runOutsideAngular(() => {
        this._animationIntervalId = setInterval(() => {
          if (welcomeEl) {
            welcomeEl.style.setProperty('--animation-tick', String(tick++));
          }
        }, 33);
      });
    }, 200);
  }

  getWelcomeData(){
    this._ttsService.getWelcomeMessage(this.token())
    .pipe(
        catchError((err) => {
            this.playWelcomeMessage();
            return throwError(() => new Error('HTTP request failed', { cause: err }));
        })
    )
    .subscribe(res => {
      this.welcomeMessageList.set(Object.values(res ?? {}));
    })
  }

  private async playWelcomeMessage() {
    try {
      const text = this.fullWelcomeMessage() ?? this.textToSpeechMock;
      const blob = await this._ttsService.getSpeechFromText(text);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);

      audio.addEventListener('play', () => {
        console.log('play start');
      }, { once: true });

      audio.addEventListener('playing', () => {
        console.log('playing');
        this.enableWelcomeMessage();
      }, { once: true });

      audio.addEventListener('ended', () => {
        URL.revokeObjectURL(url);
      }, { once: true });

      audio.addEventListener('error', (e) => {
        console.error('audio error', e);
        this.enableWelcomeMessage();
      }, { once: true });

      await audio.play();
    } catch (e) {
      console.error('TTS error', e);
      this.enableWelcomeMessage();
    }
  }
  
  private _syncronizeHomeInitialization() {
    this._websocketLaravelReverbService
      .sendMessage(
        CHANNEL_ATRIL_PANTALLA_NAME, 
        SHOW_HOME_PAGE_EVENT + this.token(), 
        []
      );
  }

  private sleep(ms: number) {
    return new Promise<void>(r => setTimeout(r, ms));
  }

  ngOnDestroy(): void {
    clearTimeout(this._fadeTimeoutId);
    clearTimeout(this._increaseIndexTimeoutId);
    clearInterval(this._typewriterIntervalId);
    clearInterval(this._animationIntervalId);
  }
  
}